import type { Message } from '../index';

import { useCallback, useMemo } from 'react';

import useLocalStorage from '@core/hooks/useLocalStorage';

const STORAGE_KEY = 'owlbot_chat_messages';
const MAX_MESSAGES = 100;

interface UseOwlbotStorage {
  clearMessages: () => void;
  loadMessages: () => Message[];
  saveMessages: (messages: Message[]) => void;
}

export function useOwlbotStorage(): UseOwlbotStorage {
  const storage = useLocalStorage({ json: true, prefix: 'owlbot' });

  const loadMessages = useCallback((): Message[] => {
    const stored = storage.getItem(STORAGE_KEY);
    if (!stored || !Array.isArray(stored)) return [];

    const messages = stored as Message[];
    return messages.slice(-MAX_MESSAGES).map(msg => ({
      ...msg,
      timestamp: new Date(msg.timestamp),
      isLoading: false,
    }));
  }, [storage]);

  const saveMessages = useCallback(
    (messages: Message[]) => {
      const messagesToStore = messages.filter(msg => !msg.isLoading && msg.content).slice(-MAX_MESSAGES);

      storage.setItem(STORAGE_KEY, messagesToStore);
    },
    [storage],
  );

  const clearMessages = useCallback(() => {
    storage.removeItem(STORAGE_KEY);
  }, [storage]);

  return useMemo(() => ({ loadMessages, saveMessages, clearMessages }), [loadMessages, saveMessages, clearMessages]);
}
